"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const config_1 = require("./config");
// ==================================================================================================
// MAIN FUNCTION
// ==================================================================================================
/**
 * Formats a number into currency format based on currency code.
 * E.g., 123456 in SGD will be formatted as S$ 1,234.56.
 *
 * @param {number} amount - The amount to be formatted without any separators (group or decimal) based on our app configuration (integer).
 * @param {CurrencyCodes} currencyCode - The currency code for the desired format.
 * @param {Options} options - Additional options for formatting.
 * @returns {string} - Formatted amount as a string.
 */
function format(amount, currencyCode, options) {
    const { symbol = currencyCode, pattern, numberOfDecimal: _numberOfDecimal, separator: { grouping, decimal }, isAllowedToBeRounded = false, } = getRawFormatterConfigByCurrencyCode(currencyCode);
    const { displaySign = 'auto', hideSymbol = false, hideFraction = false, customConfig = {}, shouldRoundDecimalValue = false, } = options !== null && options !== void 0 ? options : {};
    const { numberOfDecimal = _numberOfDecimal } = customConfig;
    const { integerPart, fractionPart, isRounded } = getCurrencyValueParts(amount, {
        numberOfDecimal,
        roundDecimalValue: shouldRoundDecimalValue && isAllowedToBeRounded,
    });
    // Formatting each part
    const formattedIntegerPart = getFormattedIntegerPart(integerPart, grouping);
    const formattedFractionPart = getFormattedFractionPart(fractionPart, numberOfDecimal);
    // Only adding fraction parts when the number of decimal is larger than 0
    const shouldShowFractionPart = numberOfDecimal > 0 && !hideFraction && !isRounded;
    const formattedAmount = `${formattedIntegerPart}${shouldShowFractionPart ? decimal + formattedFractionPart : ''}`;
    const sign = amount >= 0 ? '+' : '-';
    const shouldShowSign = displaySign === 'always' || (displaySign === 'auto' && amount < 0);
    const shouldShowSymbol = !hideSymbol && symbol !== '';
    // Constructing the final result based on the pattern
    // Replacing placeholders with the corresponding values
    // %a: Formatted amount
    // %s: Currency symbol
    const result = pattern
        .replace('%a', formattedAmount)
        .replace('%s', shouldShowSymbol ? symbol : '')
        .trim();
    return shouldShowSign ? `${sign}${result}` : result;
}
/**
 * Normalize a number into BE standard integer based on its decimal points from currency code
 * E.g., 123456 in SGD will be normalized as 12345600.
 *
 * @param {number} amount - The amount to be normalized.
 * @param {CurrencyCodes} currencyCode - The currency code for the desired format.
 * @returns {number} - Normalized amount as number.
 */
function normalizeCurrencyValue(amount, currencyCode) {
    const { numberOfDecimal } = getFormatterConfigByCurrencyCode(currencyCode);
    const multiplier = Math.pow(10, numberOfDecimal);
    return amount * multiplier;
}
/**
 * Get formatter config by currency code
 * @param currencyCode
 * @returns
 */
function getFormatterConfigByCurrencyCode(currencyCode) {
    var _a;
    const { pattern, ...selectedConfig } = getRawFormatterConfigByCurrencyCode(currencyCode);
    return {
        ...selectedConfig,
        symbol: (_a = selectedConfig.symbol) !== null && _a !== void 0 ? _a : currencyCode,
        symbolPosition: pattern.startsWith('%s') ? 'head' : 'tail',
    };
}
// ==================================================================================================
// UTILS
// ==================================================================================================
/**
 * Get formatter config by currency code
 * @param currencyCode
 * @returns
 */
function getRawFormatterConfigByCurrencyCode(currencyCode) {
    var _a;
    return (_a = config_1.configs[currencyCode]) !== null && _a !== void 0 ? _a : config_1.defaultConfig;
}
/**
 * Extracting integer and fraction parts for formatting
 * @param amount
 *  - Amount to be extracted
 * @param options
 *  - numberOfDecimal: Determines the number of decimal points
 *  - roundDecimalValue: Determines whether to round the integer part
 */
function getCurrencyValueParts(amount, options) {
    const { numberOfDecimal, roundDecimalValue } = options;
    // To prevent miscalculation amount should be a positive integer
    const roundedAmout = Math.round(amount);
    const absoluteAmount = Math.abs(roundedAmout);
    // Extracting integer and fraction parts
    let integerPart = Math.floor(absoluteAmount / Math.pow(10, numberOfDecimal));
    let fractionPart = absoluteAmount % Math.pow(10, numberOfDecimal);
    // Applying minified rule if necessary
    // Rounding up the integer part if the fraction part is >= 50
    // Integer part will have a minimum value of 1
    // E.g.
    // - 12345 with 2 decimal points will be 12300 (123.45 -> 123.00)
    // - 12390 with 2 decimal points will be 12400 (123.90 -> 124.00)
    // - 12 with 2 decimal points will be 100 (0.12 -> 1.00)
    if (roundDecimalValue) {
        integerPart = fractionPart >= 50 ? integerPart + 1 : Math.max(integerPart, 1);
        fractionPart = 0;
    }
    return {
        integerPart,
        fractionPart,
        isRounded: roundDecimalValue,
    };
}
/**
 * Format integer part by applying grouping separator
 * E.g., 123456789 with "," will be formatted as 123,456,789
 * @param integerPart - Integer part to be formatted
 * @param separator - Grouping separator
 */
function getFormattedIntegerPart(integerPart, separator) {
    // Using locale string to format integer part
    const localeStringBase = separator === ',' ? 'en-US' : 'id-ID';
    return integerPart.toLocaleString(localeStringBase);
}
/**
 * Format fraction part by applying leading zeros
 * E.g., fraction 1 with 2 decimal points will be formatted as 01
 * @param fractionPart - Fraction part to be formatted
 * @param numberOfDecimal - Number of decimal points
 */
function getFormattedFractionPart(fractionPart, numberOfDecimal) {
    return String(fractionPart).padStart(numberOfDecimal, '0');
}
exports.default = {
    format,
    normalizeCurrencyValue,
    getFormatterConfigByCurrencyCode,
};
